"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import { usePathname, useRouter } from "next/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Link from "next/link";
import { FaUsers, FaBars, FaTimes, FaSignOutAlt, FaSearch, FaBullhorn } from "react-icons/fa";
import { clearAuthData } from '@/utils/auth';

import AdminHeader from "@/components/admin/admin";
import MarketingHeader from "@/components/header";
import Footer from "@/components/footer";
import useAuthV2 from '@/hooks/useAuthV2';

const queryClient = new QueryClient();

export default function Layout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const { initializeAuth, requireAdmin, isAdmin } = useAuthV2();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const isRootDashboard = pathname === '/dashboard' || pathname === '/dashboard/';

  useEffect(() => {
    // 管理者配下ページのみアクセス制御（/dashboard 直下は公開）
    if (!isRootDashboard) {
      requireAdmin('/');
    }
  }, [requireAdmin, isRootDashboard]);

  const handleLogout = () => {
    // Clear auth tokens or session storage as needed
    clearAuthData();
    // Optionally clear other stored data here

    // Redirect to login page or home
    router.push("/dashboard");
  };

  const showAdminChrome = isAdmin() && !isRootDashboard;

  return (
    <QueryClientProvider client={queryClient}>
      {/* Ensure GTM loads on dashboard routes as well */}
      <Script id="gtm-dashboard" strategy="beforeInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-NDQKK7M5');`}
      </Script>
      {showAdminChrome ? (
        <div className="w-full min-h-screen flex flex-col overflow-x-hidden">
          {/* Header */}
          <div className="w-full flex flex-col">
            <AdminHeader />
            {/* Mobile menu toggle */}
            <button
              className="md:hidden text-primary-default"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>

          <div className="flex flex-1 relative">
            {/* Sidebar */}
            <aside
              className={`absolute md:static top-0 left-0 h-full md:h-auto w-[250px] bg-[#F2F2F2] z-50 transform transition-transform duration-300 ease-in-out
              ${menuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
            >
              <nav className="flex flex-col gap-2 p-4 md:p-4">
                {AdminMenuOptions.map((_item) => (
                  <Link
                    href={_item.url}
                    key={`admin-menu-option-${_item.label}`}
                    className={`py-3 px-3 flex flex-row items-center rounded-md cursor-pointer text-primary-default transition-colors
                      ${
                        pathname.includes(_item.url)
                          ? "bg-gray-300"
                          : "hover:bg-gray-200"
                      }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <FaUsers className="w-[24px] h-[24px]" />
                    <span className="ml-4 text-base md:text-lg">{_item.label}</span>
                  </Link>
                ))}

                {/* External Tools */}
                <div className="border-t border-gray-300 mt-3 pt-3">
                  <span className="px-3 text-xs text-gray-500 font-medium">ツール</span>
                  {ExternalMenuOptions.map((_item) => (
                    <a
                      href={_item.url}
                      key={`external-menu-option-${_item.label}`}
                      className="py-3 px-3 flex flex-row items-center rounded-md cursor-pointer text-primary-default hover:bg-gray-200 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      <_item.icon className="w-[24px] h-[24px]" />
                      <span className="ml-4 text-base md:text-lg">{_item.label}</span>
                    </a>
                  ))}
                </div>

                {/* Logout Button */}
                <button
                  type="button"
                  onClick={() => {
                    handleLogout();
                    setMenuOpen(false);
                  }}
                  className="py-3 px-3 flex flex-row items-center rounded-md cursor-pointer text-black hover:bg-gray-200 transition-colors mt-4 w-full"
                >
                  <FaSignOutAlt className="w-[24px] h-[24px]" />
                  <span className="ml-4 text-base md:text-lg">ログアウト</span>
                </button>
              </nav>
            </aside>

            {/* Main content */}
            <main className="flex-1 px-4 py-4 md:px-[20px] md:py-[20px] overflow-x-hidden">
              {children}
            </main>
          </div>
        </div>
      ) : (
        // 非管理者、または /dashboard 直下はマーケ用ヘッダーを表示
        <div className="w-full min-h-screen flex flex-col overflow-x-hidden">
          <MarketingHeader />
          <main className="flex-1">{children}</main>
          <Footer />
        </div>
      )}
    </QueryClientProvider>
  );
}

const AdminMenuOptions = [
  {
    label: "求職者一覧",
    url: "/dashboard/seekers",
  },
  {
    label: "企業一覧",
    url: "/dashboard/companies",
  },
  {
    label: "診断結果",
    url: "/dashboard/diagnosis",
  },
  {
    label: "分析",
    url: "/dashboard/analytics",
  },
  {
    label: "コラム管理",
    url: "/dashboard/columns",
  },
  {
    label: "FV Studio",
    url: "/dashboard/editor/fv/studio",
  },
  {
    label: "LP切り替え",
    url: "/dashboard/editor/lp",
  },
  {
    label: "営業ログ",
    url: "/dashboard/sales-log",
  },
  {
    label: "一斉メール",
    url: "/dashboard/bulk-email",
  },
  {
    label: "案件キュレーション",
    url: "/dashboard/curation",
  },
  {
    label: "供給開拓BD",
    url: "/dashboard/supply",
  },
];

const ExternalMenuOptions = [
  {
    label: "求人検索",
    url: "/collected-jobs",
    icon: FaSearch,
  },
  {
    label: "営業ツール",
    url: "/sales/truemee_sales_v2.html",
    icon: FaBullhorn,
  },
];
